/**
 * "Your licence has expired" / "read-only" - the one wording for every
 * licence surface.
 *
 * An expired org holds zero sections, so without this every lock it meets
 * reads "your plan does not include Matters": true, and no help at all. The
 * overlay, the gate hook's modal and the 403 handler all say "expired"
 * instead, and say it differently by role: an admin can renew, a member
 * cannot and is pointed at the person who can.
 */
import { translate } from '../../../i18n/translate';

/**
 * A licence date for a sentence ("on 12 November 2026"), in the UI language.
 * Empty for a missing or unparseable date, so a caller can fall back.
 */
export function formatLicenseDate(iso: string | null | undefined, lang?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  try {
    return d.toLocaleDateString(lang || undefined, { year: 'numeric', month: 'long', day: 'numeric' });
  } catch {
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
  }
}

/** The `category` a `license-request` modal carries for a renewal, not an upgrade. */
export const RENEWAL_CATEGORY = 'renewal';

/**
 * The `category` for ADDON_NOT_ASSIGNED: the org HAS bought the add-on, this
 * user just holds no seat on it. Nothing to buy - an admin assigns a seat, a
 * member asks one to.
 */
export const ASSIGN_CATEGORY = 'assign';

export function renewalDescription({
  isOrgAdmin,
  feature,
  readOnly = false,
}: {
  isOrgAdmin: boolean;
  /** Already-translated label of what the user tried to reach, if any. */
  feature?: string;
  /**
   * The org is read-only (state `read_only`, or a LICENSE_READ_ONLY 403):
   * say what still works - viewing and exporting - instead of "expired".
   */
  readOnly?: boolean;
}): string {
  const lead = readOnly
    ? translate('Read-only: you can view and export existing data, but not create or change anything.')
    : feature
    ? translate("Your organization's license has expired, so <strong>{{feature}}</strong> is unavailable.", {
        feature,
      })
    : translate('Your license has expired.');
  const next = isOrgAdmin
    ? translate('Renew your license to restore full access.')
    : translate('Contact your administrator to renew it.');
  return `${lead} ${next}`;
}
